import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FileClock } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../../components/ui/tooltip";
import AuditPageHeader from "../../../../components/audit/AuditPageHeader";
import { AuditSessionTabs } from "../../../../components/audit/AuditSessionTabs";
import { ControlSessionEnvelope } from "../../../../models/audit/controlSession";
import { EventRunsPopover } from "./EventRunsPopover";
import { WorkpaperFocusBar } from "./WorkpaperFocusBar";
import { WorkpaperFocusControls } from "./WorkpaperFocusControls";
import { useSessionRunsContext } from "../../../../contexts/sessionRunsContext";
import { useAudit } from "../../../../contexts/auditContext";
import { useAuth } from "../../../../contexts/authContext";
import { PermissionGate } from "../../../../components/PermissionGate";
import { hasPermission } from "../../../../lib/permissions";
import { useControl } from "../../../../hooks/audit/useControl";
import { useHookCache } from "../../../../lib/hookCache";
import { ProcedureSessionTableShell } from "../../../../models/audit/procedureSessionTableShell";
import { cn } from "../../../../lib/utils";
import { AuditLogSheet } from "../../../../components/audit/auditLog/AuditLogSheet";
import { GENERATE_ANALYTICS_SIGN_IN_REQUIRED_COPY } from "../../../../hooks/useHybridTestingAvailability";
import { useSignedInWithOptro } from "../../../../hooks/useSignedInWithOptro";
import { AnalyticsColumnProbe } from "./AnalyticsColumnProbe";

interface AuditWorkpaperLeftPanelProps {
  controlSession: ControlSessionEnvelope;
  isProcessing: boolean;
  onGenerateWorkPaper: () => void;
  /** True while a cell fold-out is open (computed by the shell via
   * `getOpenFoldOut`) — collapses the full header to the slim focus bar. */
  focusMode: boolean;
  children?: ReactNode;
}

/**
 * Work-paper chrome. Renders the page header, audit-session tabs, the
 * Generate Workpaper button, and the EventRunsPopover, then yields its
 * `children` slot to a `flex flex-col gap-4` stack of
 * `<ProcedureSessionWorkPaper>` instances (one per enabled procedure
 * session) rendered by the thin shell.
 *
 * `useControl(controlId)` is retained here — hydrated `attributes` and
 * `keyReportItems` are not on the envelope or the table-shell, so any
 * chrome-level access to them must still go through this Phase-2 bridge.
 */
export const AuditWorkpaperLeftPanel = ({
  controlSession,
  isProcessing,
  onGenerateWorkPaper,
  focusMode,
  children,
}: AuditWorkpaperLeftPanelProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { sessionRuns, getSessionRunsForEventRun, highlightCellsForEventRun } =
    useSessionRunsContext();
  const { setCurrentSelection } = useAudit();
  const { workspaceMember } = useAuth();
  const cache = useHookCache();

  // Focus mode: a cell fold-out is open, so the three-pane workspace wants the
  // full viewport height. We collapse the full page header (breadcrumb, title,
  // metadata, tabs) down to a slim identity bar. The full header comes back
  // only via the bar's explicit chevron toggle, which swaps the bar for the
  // full header inline (the workspace reflows) until toggled closed again —
  // no hover behavior, so nothing moves by accident. State lives here so the
  // choice survives cell-selection changes within a page visit. The shell
  // computes `focusMode` from `getOpenFoldOut`, so a selection that opens no
  // fold-out (flag off, doc-less support cell, check-result sheet) keeps the
  // normal full header.
  const [headerExpanded, setHeaderExpanded] = useState(false);
  // Audit Log drawer — launched from the header actions cluster. The explicit
  // header toggle has no hover machinery to suppress, so plain state suffices.
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  // `useControl` still required for chrome-level access to hydrated
  // attributes / keyReportItems (the envelope + table-shell do not carry
  // either). Per `instructions.md` this Phase-2 bridge stays until BE
  // extends the envelope `control` field.
  useControl(controlSession?.controlId);
  const lastHighlightedEventRunId = useRef<string | null>(null);

  const enabledProcedureSessions = controlSession.procedureSessions
    .filter((ps) => ps.procedure.isEnabled)
    .sort(
      (a, b) =>
        (a.procedure.procedureOrder || 0) - (b.procedure.procedureOrder || 0),
    );

  // Without the Optro sign-in, Generate still runs analytics columns; its
  // tooltip asks for the sign-in, but only when a procedure here has an
  // analytics column for it to be about. Keyed on the sign-in alone, not the
  // authoring flags: existing columns run with those off too.
  const signedInWithOptro = useSignedInWithOptro();
  const [analyticsProcedureSessionIds, setAnalyticsProcedureSessionIds] =
    useState<ReadonlySet<string>>(new Set());
  const handleAnalyticsProbe = useCallback(
    (procedureSessionId: string, hasAnalyticsColumn: boolean) => {
      setAnalyticsProcedureSessionIds((previous) => {
        if (previous.has(procedureSessionId) === hasAnalyticsColumn) {
          return previous;
        }
        const next = new Set(previous);
        if (hasAnalyticsColumn) next.add(procedureSessionId);
        else next.delete(procedureSessionId);
        return next;
      });
    },
    [],
  );
  const showGenerateAnalyticsNote =
    !signedInWithOptro &&
    enabledProcedureSessions.some((procedureSession) =>
      analyticsProcedureSessionIds.has(procedureSession.id),
    );

  // Column id -> display name, for the audit log's run details. Reads the same
  // cached table-shells the highlight machinery below uses, so only procedures
  // the user has visited resolve; unknown ids are dropped by the consumer
  // rather than rendered raw. Run payloads carry attribute-test-column ids
  // while the shell keys on procedure-column ids, so both are matched.
  const columnNameById = useCallback(
    (columnId: string): string | undefined => {
      const column = enabledProcedureSessions
        .flatMap(
          (procedureSession) =>
            cache.getEntry<ProcedureSessionTableShell>([
              "procedure-session",
              procedureSession.id,
              "table-shell",
            ])?.data?.procedure.columns ?? [],
        )
        .find(
          (candidate) =>
            candidate.id === columnId ||
            candidate.attributeTestColumn?.id === columnId,
        );
      return (
        column?.attributeTestColumn?.name ??
        column?.evaluatedColumn?.name ??
        column?.supportColumn?.name ??
        undefined
      );
    },
    [enabledProcedureSessions, cache],
  );

  // Highlight cells on load if eventRunId is in URL.
  useEffect(() => {
    const eventRunIdParam = searchParams.get("eventRunId");
    if (!eventRunIdParam) {
      lastHighlightedEventRunId.current = null;
      return;
    }
    if (lastHighlightedEventRunId.current === eventRunIdParam) return;
    if (!controlSession || enabledProcedureSessions.length === 0) return;
    if (sessionRuns.length === 0) return;

    const eventRunSessionRuns = getSessionRunsForEventRun(eventRunIdParam);
    if (eventRunSessionRuns.length > 0) {
      lastHighlightedEventRunId.current = eventRunIdParam;
      highlightCellsForEventRun(eventRunIdParam);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("eventRunId");
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [
    searchParams,
    setSearchParams,
    controlSession,
    enabledProcedureSessions.length,
    sessionRuns,
    getSessionRunsForEventRun,
    highlightCellsForEventRun,
  ]);

  // The full page header — breadcrumb + title + metadata + tabs + actions. In
  // browse mode it sits inline; in focus mode it swaps in via the explicit
  // header toggle.
  const fullHeaderBlock = (
    <>
      <AuditPageHeader extraPageLabel="Workpaper View" />
      {/* Tabs on the left; the Generate/Download actions sit on the right of
          this same row, equi-spaced to the left of the background-tasks
          (EventRunsPopover) icon. */}
      <div className="mt-4 mb-4 flex items-center gap-2">
        <AuditSessionTabs activeTab="workpaper" v2={true} />
        <div className="ml-auto flex items-center gap-2">
          {controlSession.status !== "complete" && (
            <PermissionGate permission="run_test">
              {/* Same pattern as Send to Optro: the sign-in reason is a
                  tooltip on the button. The span keeps it reachable while
                  the button is disabled for a run in progress. */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    tabIndex={
                      showGenerateAnalyticsNote && isProcessing ? 0 : undefined
                    }
                    className="inline-flex"
                  >
                    <Button
                      onClick={onGenerateWorkPaper}
                      loading={isProcessing}
                      variant="outline"
                    >
                      Generate Workpaper
                    </Button>
                  </span>
                </TooltipTrigger>
                {showGenerateAnalyticsNote && (
                  <TooltipContent data-testid="generate-workpaper-analytics-tooltip">
                    {GENERATE_ANALYTICS_SIGN_IN_REQUIRED_COPY}
                  </TooltipContent>
                )}
              </Tooltip>
            </PermissionGate>
          )}
          {hasPermission(workspaceMember, "view_audit_logs") && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant="outline"
                    data-testid="workpaper-audit-log-button"
                    onClick={() => setAuditLogOpen(true)}
                  >
                    <FileClock className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Audit Log</TooltipContent>
              </Tooltip>
              <AuditLogSheet
                open={auditLogOpen}
                onOpenChange={setAuditLogOpen}
                controlSessionId={controlSession.id}
                sessionName={controlSession.name}
                columnNameById={columnNameById}
              />
            </>
          )}
          <EventRunsPopover controlSessionId={controlSession.id} />
        </div>
      </div>
    </>
  );

  return (
    <div className="h-full flex flex-col overflow-hidden relative">
      {!signedInWithOptro &&
        enabledProcedureSessions.map((procedureSession) => (
          <AnalyticsColumnProbe
            key={procedureSession.id}
            procedureSessionId={procedureSession.id}
            onChange={handleAnalyticsProbe}
          />
        ))}
      {focusMode ? (
        // Focus mode — collapsed, the slim identity bar holds the page
        // height; expanded, the full header replaces the bar wholesale. The
        // chevron + exit cluster renders at the top-left in both states so
        // the controls never move when toggling. Both states stay mounted in
        // 0fr/1fr grid sections so the swap animates height smoothly without
        // measuring content; `invisible` (transitioned) keeps the hidden
        // state out of the tab order once the animation lands.
        <div className="flex-shrink-0">
          <div
            className={cn(
              "grid transition-[grid-template-rows,opacity,visibility] duration-300 ease-out",
              headerExpanded
                ? "grid-rows-[0fr] opacity-0 invisible"
                : "grid-rows-[1fr] opacity-100",
            )}
          >
            <div className="overflow-hidden">
              <WorkpaperFocusBar
                sessionName={controlSession.name}
                status={controlSession.status}
                onToggleHeader={() => setHeaderExpanded(true)}
                onExit={() => setCurrentSelection(undefined)}
              />
            </div>
          </div>
          <div
            className={cn(
              "grid transition-[grid-template-rows,opacity,visibility] duration-300 ease-out",
              headerExpanded
                ? "grid-rows-[1fr] opacity-100"
                : "grid-rows-[0fr] opacity-0 invisible",
            )}
          >
            <div className="overflow-hidden">
              <div className="border-b border-stone-200 px-4 pt-3 pb-4">
                <WorkpaperFocusControls
                  headerExpanded
                  onToggleHeader={() => setHeaderExpanded(false)}
                  onExit={() => setCurrentSelection(undefined)}
                />
                <div className="mt-3">{fullHeaderBlock}</div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-shrink-0 px-4 pt-4">{fullHeaderBlock}</div>
      )}

      <div className="flex-1 overflow-hidden min-h-0 px-4 pb-4 pt-2">
        {/* No loading gate here: the shell only renders this panel once the
            envelope is present, and background envelope refetches (run
            completion → ["control-session", id] invalidate) keep the cached
            data populated — gating on the loading flag would unmount the
            whole table stack and flash a spinner over an invisible refetch
            (same hazard documented on AuditSessionPageV2's gate). */}
        {children}
      </div>
    </div>
  );
};
